import { EVoucher } from "../models/evoucher";
import { ChannelEnum } from "../constants/enums";

// SKUList is the read-only list returned by GET endpoints; EVoucherSkus and
// SKU are fallbacks for responses where SKUList wasn't populated.
export const getCampaignEVoucherSkus = (data?: {
  SKU?: string;
  SKUList?: string[] | null;
  EVoucherSkus?: { SKU: string }[];
} | null): string[] => {
  if (data?.SKUList?.length) return data.SKUList;
  if (data?.EVoucherSkus?.length) return data.EVoucherSkus.map((e) => e.SKU);
  if (data?.SKU) return [data.SKU];
  return [];
};

export function htmlToWhatsappText(html: string): string {
  const doc = new DOMParser().parseFromString(html || "", "text/html");

  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || "").replace(/\s+/g, " ");
    }
    if (!(node instanceof HTMLElement)) return "";

    const tag = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes).map(walk).join("");

    switch (tag) {
      case "br":
        return "\n";
      case "p":
        return children.trim() + "\n";
      case "strong":
      case "b":
        return children ? `*${children.trim()}*` : "";
      case "em":
      case "i":
        return children ? `_${children.trim()}_` : "";
      case "s":
      case "del":
      case "strike":
        return children ? `~${children.trim()}~` : "";
      case "code":
      case "pre":
        return children ? `\`\`\`${children}\`\`\`` : "";
      case "ul": {
        const lines = Array.from(node.querySelectorAll(":scope > li")).map(
          (li) => "• " + walk(li).trim(),
        );
        return lines.join("\n") + "\n";
      }
      case "ol": {
        const items = Array.from(node.querySelectorAll(":scope > li"));
        let orderedIdx = 1;
        const lines = items.map((li) => {
          const isBullet =
            (li as HTMLElement).getAttribute("data-list") === "bullet";
          return isBullet
            ? "• " + walk(li).trim()
            : `${orderedIdx++}. ${walk(li).trim()}`;
        });
        return lines.join("\n") + "\n";
      }
      case "li":
        return children;
      case "a": {
        const href = (node.getAttribute("href") || "").trim();
        const text = children.trim();
        if (href && text && text !== href) return `${text} ${href}`;
        return href || text;
      }
      default:
        return children;
    }
  };
  const result = walk(doc.body)
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return result;
}

export function removeLink(html: string): string {
  return html
    .replace(/\r\n/g, "\n")
    .replace(
      /\n{1,2}\s*[^\n]+:\n[^\n]+\n\nTo Unsub:[\s\S]*?https:\/\/getz\.co\/a\/UNSUB/g,
      "",
    )
    .replace(/\n{1,2}Update here:\n[^\n]+\s*$/, "")
    .trim();
}

export function removeWhatsappAdditionalMessage(text: string): string {
  const markerIndexes = [
    text.indexOf("\nUpdate your profile here:"),
    text.indexOf("\n👉 "),
  ].filter((idx) => idx !== -1);
  if (markerIndexes.length === 0) return text.trim();
  return text.slice(0, Math.min(...markerIndexes)).trim();
}

// Builds the <li> run for a list block matched by whatsappTextToHtml's
// ordered/unordered list steps. A blank line between two source items (e.g.
// the backend-generated "1. Last Name\n\n2. Birthday" for
// IncompleteProfileFields) is dropped from the text but flagged via the
// "wa-li-gap" class so CSS can still render it as a visible blank row —
// tight, single-newline-separated lists (e.g. from the rich text editor)
// get no such gap.
function buildListItems(block: string, markerPattern: RegExp): string {
  let items = "";
  let sawItem = false;
  let pendingGap = false;

  for (const rawLine of block.split("\n")) {
    const line = rawLine.trim();
    if (!line) {
      if (sawItem) pendingGap = true;
      continue;
    }
    const match = markerPattern.exec(line);
    if (!match) continue;
    const gapClass = pendingGap ? ' class="wa-li-gap"' : "";
    items += `<li${gapClass}>${match[1]}</li>`;
    pendingGap = false;
    sawItem = true;
  }

  return items;
}

export function whatsappTextToHtml(text: string): string {
  if (!text) return "";

  let html: string = text;

  // 1️⃣ Normalize escaped newlines → real newlines
  html = html.replace(/\\n/g, "\n");

  // 2️⃣ Escape HTML (prevent injection)
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;");

  // 3️⃣ Code block (multiline-safe)
  html = html.replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>");

  // 4️⃣ Text formatting
  html = html.replace(/\*(.*?)\*/g, "<b>$1</b>");
  html = html.replace(/_(.*?)_/g, "<i>$1</i>");
  html = html.replace(/~(.*?)~/g, "<s>$1</s>");

  // 5️⃣ Ordered list
  // Items may be separated by a blank line as well as a plain single
  // newline — `\n+` swallows either so the whole run becomes one <ol>
  // instead of splitting into several single-item lists that each restart
  // their numbering at 1.
  html = html.replace(
    /(^|\n)(\d+\.\s.*(?:\n+\d+\.\s.*)*)/g,
    (_: string, prefix: string, block: string): string =>
      `${prefix}<ol>${buildListItems(block, /^\d+\.\s*(.*)$/)}</ol>`,
  );

  // 6️⃣ Unordered list (same blank-line tolerance as the ordered list above)
  html = html.replace(
    /(^|\n)([-•]\s.*(?:\n+[-•]\s.*)*)/g,
    (_: string, prefix: string, block: string): string =>
      `${prefix}<ul>${buildListItems(block, /^[-•]\s*(.*)$/)}</ul>`,
  );

  // 7️⃣ URLs → clickable links
  // getz.co/a/CTALINK is excluded: it's a static placeholder for the
  // per-member JWT click-tracking link (GXT-179), not a real destination.
  html = html.replace(
    /(https?:\/\/(?!getz\.co(?:\/a)?\/(?:UNSUB|CTALINK))[^\s]+)/g,
    `<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>`,
  );

  // 🔒 8️⃣ Protect block boundaries
  html = html
    .replace(/<\/(ul|ol|pre)>/g, "</$1>\n")
    .replace(/<(ul|ol|pre)>/g, "\n<$1>");

  // 9️⃣ Convert newlines → <br />
  html = html
    .replace(/\n{2,}/g, "<br /><br />") // paragraph spacing
    .replace(/\n/g, "<br />");

  // 🔧 10️⃣ Cleanup invalid spacing around lists
  html = html
    .replace(/(<\/(ul|ol)>)<br \/>/g, "$1")
    .replace(/<br \/><(ul|ol)>/g, "<$1>");

  return html.trim();
}

// Formats a raw message body (e.g. CampaignMemberLog.Content or
// CampaignDetails.Messages[0].Content) into safe-to-render HTML, per channel.
export function formatMessageContent(
  text: string,
  channel?: ChannelEnum | number | null,
): string {
  if (!text) return "";

  if (channel === ChannelEnum.WhatsAppPremium) {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/\\n/g, "\n")
      .replace(/\n\n/g, "<br /><br />")
      .replace(/\n/g, "<br />")
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>");
  }

  return whatsappTextToHtml(text);
}

export const mapToEVouchers = (payload: any): EVoucher[] => {
  const list = Array.isArray(payload?.eVouchers)
    ? payload.eVouchers
    : Array.isArray(payload)
      ? payload
      : [];

  return list.map((x: any): EVoucher => {
    const outlets =
      Array.isArray(x.outlets) && x.outlets.length
        ? x.outlets
            .map((o: any) => o?.outletName)
            .filter(Boolean)
            .join(", ")
        : "";

    return {
      Id: String(x.id ?? ""),
      Name: String(x.title ?? ""),
      Outlet: outlets,
      Price: Number(x.price ?? 0),
      SKU: String(x.sku ?? ""),
      StartDate: x?.validityPeriod?.startDateRange
        ? new Date(x.validityPeriod.startDateRange)
        : null,
      EndDate: x?.validityPeriod?.endDateRange
        ? new Date(x.validityPeriod.endDateRange)
        : null,
      ModifiedDate: x?.ModifiedDate ? new Date(x.ModifiedDate) : null,
      Status: "Active",
    };
  });
};
